/* ============================================================================
   HIYAZAKURA — shell interaction layer  (agent A4)
   Owns: DOM population from copy.json, the chill slider, the blossom driver,
         pack selection, and live Shopify commerce.

   Two rules this file never breaks:
     1. A price is only ever displayed if it came back from Shopify just now.
        No stored price, no fallback price, no guess. Failure => purchase blocked.
     2. No section is ever left empty. index.html ships the full, real copy;
        copy.json only ever *replaces* text it actually provides.
   ========================================================================== */

const doc = document;
const root = doc.documentElement;

const $  = (sel, scope = doc) => scope.querySelector(sel);
const hz = (name, scope = doc) => scope.querySelector('[data-hz="' + name + '"]');

const PACK_KEYS = ['one', 'pair', 'trio'];

/* Temperature model ---------------------------------------------------------
   Slider runs left (warm) to right (cold). 17 C is the line the product is
   built around, so it is also the line the UI is built around.            */
const WARM_C  = 22;   // room temperature
const COLD_C  = 0;    // ice-cold
const BLOOM_C = 17;
const BLOOM_P = (WARM_C - BLOOM_C) / (WARM_C - COLD_C);   // slider position of 17 C
const SETTLE_P = 0.83;                                    // where the autoplay comes to rest

const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
const reducedMotion = () => motionQuery.matches;

const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);
const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const isText = v => typeof v === 'string' && v.trim().length > 0;
const textOf = v => (isText(v) ? v.trim() : '');

/* ==========================================================================
   1 · COPY
   ========================================================================== */

const setText = (el, value) => { if (el && isText(value)) el.textContent = value.trim(); };

/* Assign only on a real change: .hz-buy__status is a live region, and rewriting it
   with an identical string makes some screen readers announce it all over again. */
const setIfChanged = (el, value) => { if (el && el.textContent !== value) el.textContent = value; };

/** Pull a readable {label, body} out of a copy entry that may be a string or an object. */
function pairOf(item) {
  if (isText(item)) return { label: '', body: item.trim() };
  if (!item || typeof item !== 'object') return { label: '', body: '' };
  return {
    label: textOf(item.title) || textOf(item.label) || textOf(item.name) || textOf(item.q),
    body:  textOf(item.text)  || textOf(item.body)  || textOf(item.copy) || textOf(item.a)
  };
}

function renderMomentSteps(items) {
  const list = hz('momentSteps');
  if (!list || !Array.isArray(items) || !items.length) return;
  const nodes = [];
  for (const item of items) {
    const { label, body } = pairOf(item);
    if (!label && !body) continue;
    const li = doc.createElement('li');
    li.className = 'hz-moment__step';
    if (label) {
      const h = doc.createElement('h3');
      h.className = 'hz-moment__step-title';
      h.textContent = label;
      li.append(h);
    }
    if (body) {
      const p = doc.createElement('p');
      p.className = 'hz-moment__step-body';
      p.textContent = body;
      li.append(p);
    }
    nodes.push(li);
  }
  nodes.forEach((li, i) => { li.dataset.step = String(i + 1); });
  if (nodes.length) list.replaceChildren(...nodes);
}

function renderCraftFacts(items) {
  const list = hz('craftFacts');
  if (!list || !Array.isArray(items) || !items.length) return;
  const nodes = [];
  for (const item of items) {
    const { label, body } = pairOf(item);
    if (!label && !body) continue;
    const li = doc.createElement('li');
    li.className = 'hz-craft__fact';
    if (label) {
      const strong = doc.createElement('strong');
      strong.className = 'hz-craft__fact-label';
      strong.textContent = label;
      li.append(strong);
    }
    if (body) {
      const span = doc.createElement('span');
      span.className = 'hz-craft__fact-text';
      span.textContent = body;
      li.append(span);
    }
    nodes.push(li);
  }
  if (nodes.length) list.replaceChildren(...nodes);
}

function renderCare(items) {
  const list = hz('careItems');
  if (!list || !Array.isArray(items) || !items.length) return;
  const nodes = [];
  for (const item of items) {
    const text = isText(item) ? item.trim() : textOf(item && item.text);
    if (!text) continue;
    const allowed = item && typeof item === 'object' ? item.allowed === true : true;
    const li = doc.createElement('li');
    li.className = allowed ? 'hz-care__item' : 'hz-care__item hz-care__item--no';
    li.dataset.allowed = String(allowed);
    /* If the restriction is phrased as a bare noun ("Microwave"), its meaning lives
       only in a CSS class — which a screen reader cannot see. Say it out loud. */
    if (!allowed && !/^(no|not|never|avoid|don|keep|steer|skip)/i.test(text)) {
      const sr = doc.createElement('span');
      sr.className = 'hz-sr';
      sr.textContent = 'Not safe: ';
      li.append(sr);
    }
    li.append(doc.createTextNode(text));
    nodes.push(li);
  }
  if (nodes.length) list.replaceChildren(...nodes);
}

function renderFaq(items) {
  const list = hz('faqItems');
  if (!list || !Array.isArray(items) || !items.length) return;
  const pairs = [];
  const nodes = [];
  for (const item of items) {
    const q = textOf(item && item.q) || textOf(item && item.question) || textOf(item && item.title);
    const a = textOf(item && item.a) || textOf(item && item.answer)   || textOf(item && item.text);
    if (!q || !a) continue;
    const wrap = doc.createElement('div');
    wrap.className = 'hz-faq__item';
    const h = doc.createElement('h3');
    h.className = 'hz-faq__q';
    h.textContent = q;
    const p = doc.createElement('p');
    p.className = 'hz-faq__a';
    p.textContent = a;
    wrap.append(h, p);
    nodes.push(wrap);
    pairs.push({ q, a });
  }
  if (!nodes.length) return;
  list.replaceChildren(...nodes);
  refreshFaqSchema(pairs);
}

/** Keep the FAQ structured data identical to what is actually on the page. */
function refreshFaqSchema(pairs) {
  const script = doc.getElementById('hz-jsonld-faq');
  if (!script || !pairs.length) return;
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: pairs.map(p => ({
      '@type': 'Question',
      name: p.q,
      acceptedAnswer: { '@type': 'Answer', text: p.a }
    }))
  }, null, 2);
}

function renderPackCopy(packsCopy) {
  const items = packsCopy && typeof packsCopy === 'object' ? packsCopy.items : null;
  if (!items || typeof items !== 'object') return;
  for (const key of PACK_KEYS) {
    const item = items[key];
    const pack = packs.get(key);
    if (!item || typeof item !== 'object' || !pack) continue;
    setText(pack.card.querySelector('.hz-pack__name'), item.name);
    setText(pack.card.querySelector('.hz-pack__note'), item.note);
    const cups = typeof item.cups === 'number' && Number.isFinite(item.cups)
      ? item.cups + (item.cups === 1 ? ' cup' : ' cups')
      : item.cups;
    setText(pack.card.querySelector('.hz-pack__cups'), cups);
    if (isText(item.name)) pack.label = item.name.trim();
  }
}

function applyCopy(copy) {
  const hero   = copy.hero   || {};
  const chillC = copy.chill  || {};
  const moment = copy.moment || {};
  const craft  = copy.craft  || {};
  const specs  = copy.specs  || {};
  const packsC = copy.packs  || {};
  const care   = copy.care   || {};
  const faq    = copy.faq    || {};
  const foot   = copy.foot   || {};

  setText(hz('heroTitle'), hero.title);
  setText(hz('heroSub'),   hero.sub);
  setText(hz('heroCta'),   hero.cta);
  setText(hz('chillHint'), chillC.hint);

  setText(hz('momentTitle'), moment.title);
  renderMomentSteps(moment.steps);

  setText(hz('craftTitle'), craft.title);
  setText(hz('craftBody'),  craft.body);
  renderCraftFacts(craft.facts);

  setText(hz('specsTitle'), specs.title);

  setText(hz('packsTitle'), packsC.title);
  setText(hz('packsSub'),   packsC.sub);
  renderPackCopy(packsC);

  setText(hz('careTitle'), care.title);
  renderCare(care.items);

  setText(hz('faqTitle'), faq.title);
  renderFaq(faq.items);

  setText(hz('footLine'), foot.line);

  renderCommerce();  // pack labels may have changed
}

async function loadCopy() {
  try {
    const res = await fetch(new URL('./extoril-hiyazakura-copy.json?v=506c74e76820cb13', import.meta.url), { credentials: 'omit' });
    if (!res.ok) throw new Error('copy.json responded ' + res.status);
    const copy = await res.json();
    if (!copy || typeof copy !== 'object') throw new Error('copy.json is not an object');
    applyCopy(copy);
    root.dataset.hzCopy = 'loaded';
  } catch (err) {
    /* index.html already carries the complete, real copy for every section.
       Leaving the served markup exactly as it is IS the fallback — which is why
       nothing here writes placeholder text and nothing can render "undefined". */
    root.dataset.hzCopy = 'builtin';
    console.warn('[hiyazakura] copy.json unavailable — using the built-in page copy.', err);
  }
}

/* ==========================================================================
   2 · CHILL SLIDER + BLOSSOM ENGINE
   ========================================================================== */

const chill = {
  el:    hz('chill'),
  range: hz('range'),
  num:   hz('tempNum'),
  state: hz('tempState'),
  /* The engine and its slider live in the mid-page "try" section now; the state classes follow them. */
  hero:  $('.hz-try') || $('.hz-hero'),
  crossed: false,
  bloom: 0,
  pulse: 0
};

const PHASE_WORD = {
  plain:   'plain white',
  first:   'first blossoms',
  opening: 'blossoms opening',
  full:    'full bloom'
};

/* Whole degrees, in both units — decimals read like an error on a product page.
   Round to what we print, then decide state from THAT number, so the readout can
   never say 17°C while the cup still claims to be plain white. */
const tempFor  = p => Math.round(WARM_C - p * (WARM_C - COLD_C));
/* 17.0 is "blossoms are here", so it gets a visible floor rather than a bloom of zero. */
const bloomFor = t => (t > BLOOM_C ? 0 : clamp(Math.max(0.06, (BLOOM_C - t) / (BLOOM_C - COLD_C)), 0, 1));
const phaseFor = b => (b <= 0 ? 'plain' : b < 0.28 ? 'first' : b < 0.75 ? 'opening' : 'full');

let cup = null;
let blossomBroken = false;

function applyChill(p) {
  p = clamp(p, 0, 1);
  const temp = tempFor(p);
  const bloom = bloomFor(temp);
  const phase = phaseFor(bloom);
  const chilled = temp <= BLOOM_C;
  chill.bloom = bloom;

  for (const el of [root, chill.el]) {
    if (!el) continue;
    el.style.setProperty('--hz-chill', p.toFixed(4));
    el.style.setProperty('--hz-chill-pct', (p * 100).toFixed(2) + '%');
    el.style.setProperty('--hz-bloom', bloom.toFixed(4));
    el.style.setProperty('--hz-temp', String(temp));
  }

  if (chill.num)   chill.num.textContent = temp + '°C';
  if (chill.state) chill.state.textContent = PHASE_WORD[phase];

  for (const el of [chill.el, chill.hero]) {
    if (!el) continue;
    el.dataset.hzPhase = phase;
    el.classList.toggle('is-chilled', chilled);
  }

  if (chill.range) {
    const f = Math.round(temp * 9 / 5 + 32);
    chill.range.setAttribute('aria-valuetext',
      temp + ' degrees Celsius, ' + f + ' Fahrenheit. ' +
      (chilled ? 'Cherry blossoms are showing: ' + PHASE_WORD[phase] + '.' : 'The cup is plain white.'));
  }

  /* Crossing 17 C is the whole product. Mark it as an event, once per crossing. */
  if (chilled !== chill.crossed) {
    chill.crossed = chilled;
    if (chilled && !reducedMotion()) markThresholdCrossed();
  }

  if (cup) {
    try { cup.setChill(bloom); }
    catch (err) { showBlossomFallback(err); }
  }
}

function markThresholdCrossed() {
  clearTimeout(chill.pulse);
  for (const el of [chill.el, chill.hero]) el && el.classList.add('is-crossing');
  chill.pulse = setTimeout(() => {
    for (const el of [chill.el, chill.hero]) el && el.classList.remove('is-crossing');
  }, 900);
}

function setChillPosition(p, fromInput) {
  p = clamp(p, 0, 1);
  if (!fromInput && chill.range) chill.range.value = String(Math.round(p * 100));
  applyChill(p);
}

function initChill() {
  if (!chill.range) return;
  chill.range.addEventListener('input', () => {
    abortAutoplay();
    setChillPosition(Number(chill.range.value) / 100, true);
  });
  setChillPosition(Number(chill.range.value) / 100 || 0, true);
}

/* --- engine ---------------------------------------------------------------- */

function showBlossomFallback(reason) {
  if (blossomBroken) return;
  blossomBroken = true;
  const handle = cup;
  cup = null;
  try { handle && handle.destroy && handle.destroy(); } catch (_) { /* already gone */ }

  const canvas = hz('canvas');
  const fallback = hz('fallback');
  const stage = hz('stage');
  if (canvas) { canvas.hidden = true; canvas.setAttribute('aria-hidden', 'true'); }
  if (fallback) fallback.hidden = false;
  if (stage) stage.dataset.hzBlossom = 'fallback';
  settleBlossom();
  console.warn('[hiyazakura] blossom engine unavailable — static fallback shown.', reason);
}

let settleBlossom = () => {};
const blossomSettled = new Promise(resolve => { settleBlossom = resolve; });

async function bootBlossom() {
  const canvas = hz('canvas');
  if (!canvas) { settleBlossom(); return; }
  if (typeof canvas.getContext !== 'function') {
    showBlossomFallback(new Error('canvas is not supported here'));
    return;
  }

  let mod;
  try {
    mod = await import(new URL('./extoril-hiyazakura-blossom.js?v=21f7a923ea9e8b00', import.meta.url).href);
  } catch (err) {
    showBlossomFallback(err);
    return;
  }

  const create = typeof mod.createBlossomCup === 'function' ? mod.createBlossomCup
               : typeof mod.default === 'function' ? mod.default
               : null;
  if (!create) {
    showBlossomFallback(new Error('blossom.js does not export createBlossomCup'));
    return;
  }

  let handle;
  try {
    handle = create(canvas, {
      seed: 0x48595A,
      /* On Shopify the theme sets window.HZ_ASSETS to the CDN urls of the
         dish images; locally the engine resolves them beside blossom.js. */
      assets: (typeof window !== 'undefined' && window.HZ_ASSETS) || undefined,
      reducedMotion: reducedMotion(),
      onReady: () => {
        const stage = hz('stage');
        if (stage) stage.dataset.hzBlossom = 'ready';
        settleBlossom();
        queueMicrotask(() => { if (cup && cup.invalidate) cup.invalidate(); });
      },
      onError: err => showBlossomFallback(err)
    });
  } catch (err) {
    showBlossomFallback(err);
    return;
  }

  if (!handle || typeof handle.setChill !== 'function') {
    showBlossomFallback(new Error('createBlossomCup returned no usable handle'));
    return;
  }
  if (blossomBroken) { try { handle.destroy && handle.destroy(); } catch (_) {} return; }

  cup = handle;
  try {
    cup.setChill(chill.bloom);
    if (cup.invalidate) cup.invalidate();
  } catch (err) {
    showBlossomFallback(err);
  }
}

/* Force a full redraw whenever the canvas could have gone stale behind our back.
   (extoril-chroma shipped a cache bug that only showed up on re-entry.) */
function revalidate() {
  if (cup && cup.invalidate) {
    try { cup.invalidate(); } catch (err) { showBlossomFallback(err); }
  }
}

function observeStage() {
  const stage = hz('stage');
  if (!stage) return;
  if (typeof IntersectionObserver !== 'function') { startAutoplay(); return; }
  let seen = false;
  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      revalidate();
      if (!seen) { seen = true; startAutoplay(); }
    }
  }, { threshold: 0.25 });
  io.observe(stage);
}

let resizeTimer = 0;
addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(revalidate, 180);
}, { passive: true });

doc.addEventListener('visibilitychange', () => { if (!doc.hidden) revalidate(); });

/* ==========================================================================
   3 · AUTOPLAY — one unprompted bloom, abandoned the moment anyone touches it
   ========================================================================== */

const autoplay = { running: false, done: false, raf: 0, timer: 0 };

function abortAutoplay() {
  autoplay.running = false;
  autoplay.done = true;
  if (autoplay.raf) { cancelAnimationFrame(autoplay.raf); autoplay.raf = 0; }
  if (autoplay.timer) { clearTimeout(autoplay.timer); autoplay.timer = 0; }
}

function pause(ms) {
  return new Promise(resolve => {
    autoplay.timer = setTimeout(() => { autoplay.timer = 0; resolve(); }, ms);
  });
}

function glide(from, to, ms) {
  return new Promise(resolve => {
    if (!autoplay.running) { resolve(false); return; }
    const t0 = performance.now();
    const step = now => {
      if (!autoplay.running) { resolve(false); return; }
      const k = clamp((now - t0) / ms, 0, 1);
      setChillPosition(from + (to - from) * easeInOutCubic(k), false);
      if (k < 1) { autoplay.raf = requestAnimationFrame(step); }
      else { autoplay.raf = 0; resolve(true); }
    };
    autoplay.raf = requestAnimationFrame(step);
  });
}

async function startAutoplay() {
  if (autoplay.done || autoplay.running) return;
  if (reducedMotion()) { autoplay.done = true; return; }          // no unprompted motion
  if (!chill.range || Number(chill.range.value) !== 0) { autoplay.done = true; return; }

  autoplay.running = true;

  // Give the engine a moment to come up, but never hang on it.
  await Promise.race([blossomSettled, pause(1400)]);
  if (!autoplay.running) return;

  await pause(450);
  if (!autoplay.running) return;

  if (!(await glide(0, BLOOM_P, 1150))) return;   // warm -> exactly 17 C
  await pause(480);                               // hold on the threshold
  if (!autoplay.running) return;
  await glide(BLOOM_P, SETTLE_P, 1600);           // and over it, into the bloom

  autoplay.running = false;
  autoplay.done = true;
}

function armAutoplayAbort() {
  const stop = () => abortAutoplay();
  addEventListener('pointerdown', stop, { passive: true, once: true });
  addEventListener('keydown', stop, { passive: true, once: true });
  try { motionQuery.addEventListener('change', stop); }
  catch (_) { if (motionQuery.addListener) motionQuery.addListener(stop); }
}

/* ==========================================================================
   4 · PACKS
   ========================================================================== */

const packs = new Map();
let selected = 'one';

function initPacks() {
  for (const key of PACK_KEYS) {
    const card = $('.hz-pack[data-pack="' + key + '"]');
    if (!card) continue;
    packs.set(key, {
      key,
      card,
      input: card.querySelector('.hz-pack__input'),
      priceEl: card.querySelector('.hz-pack__price'),
      serverMoney: null,
      serverCents: null,
      serverWas: null,
      serverWasCents: null,
      dealEl: card.querySelector('.hz-pack__deal'),
      wasEl: card.querySelector('.hz-pack__was'),
      saveEl: card.querySelector('.hz-pack__save'),
      label: (card.querySelector('.hz-pack__name') || {}).textContent
        ? card.querySelector('.hz-pack__name').textContent.trim()
        : key.toUpperCase(),
      variantId: null,
      handle: null,
      productUrl: null,
      price: null,
      available: false,
      reason: 'pending'
    });
  }

  /* Shopify's money_with_currency format is theme-configurable — symbol, separators and a
     trailing currency code all vary — and Intl cannot reproduce it. When the live figure
     matches what Liquid already printed, reuse the
     server's own string verbatim — same number, no reformatting flicker on every load. */
  for (const [, pack] of packs) {
    const readServer = (el, moneyKey, centsKey) => {
      if (!el) return;
      const cents = Number(el.getAttribute('data-hz-cents'));
      const money = el.getAttribute('data-hz-money');
      if (Number.isFinite(cents) && cents > 0 && isText(money)) {
        pack[centsKey] = cents;
        pack[moneyKey] = money.trim();
      }
    };
    readServer(pack.priceEl, 'serverMoney', 'serverCents');
    readServer(pack.wasEl, 'serverWas', 'serverWasCents');
  }

  const checked = $('.hz-pack__input:checked');
  if (checked && packs.has(checked.value)) selected = checked.value;

  const grid = hz('packGrid');
  if (!grid) return;

  /* Native radios give us arrow-key navigation, Space activation and radio
     semantics for free; we only add the class toggle and an Enter shortcut. */
  grid.addEventListener('change', event => {
    const input = event.target && event.target.closest ? event.target.closest('.hz-pack__input') : null;
    if (input) selectPack(input.value);
  });
  grid.addEventListener('click', event => {
    const card = event.target && event.target.closest ? event.target.closest('.hz-pack') : null;
    if (card && card.dataset.pack) selectPack(card.dataset.pack);
  });
  grid.addEventListener('keydown', event => {
    if (event.key !== 'Enter') return;
    const card = event.target && event.target.closest ? event.target.closest('.hz-pack') : null;
    if (!card || !card.dataset.pack) return;
    event.preventDefault();
    selectPack(card.dataset.pack);
  });
}

function selectPack(key) {
  if (!packs.has(key)) return;
  selected = key;
  for (const [k, pack] of packs) {
    const on = k === key;
    pack.card.classList.toggle('hz-pack--selected', on);
    if (pack.input && pack.input.checked !== on) pack.input.checked = on;
  }
  renderCommerce();
}

/* ==========================================================================
   5 · COMMERCE — live price or no price at all
   ========================================================================== */

const commerce = {
  storeOrigin: null,
  currency: null,
  productUrl: 'https://extoril.com/products/extoril-hiyazakura',
  lastError: null,
  inflight: null
};

/** Unwrap 123, "123", or "gid://shopify/ProductVariant/123" to "123". Never invents one. */
function variantIdOf(value) {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) return String(Math.trunc(value));
  if (typeof value !== 'string') return null;
  const match = value.match(/(\d{4,})\s*$/);
  return match ? match[1] : null;
}

function originOf(value) {
  if (!isText(value)) return null;
  try { return new URL(value.trim(), location.href).origin; } catch (_) { return null; }
}

/** commerce.json has not been written yet, so accept every shape it might sanely take. */
function packEntriesFrom(json) {
  const out = {};
  const take = (key, entry) => {
    if (typeof key !== 'string') return;
    const k = key.toLowerCase();
    if (PACK_KEYS.indexOf(k) === -1 || !entry || out[k]) return;
    out[k] = entry;
  };
  for (const field of ['packs', 'variants', 'items', 'products']) {
    const value = json[field];
    if (Array.isArray(value)) {
      for (const entry of value) take(entry && (entry.key || entry.pack || entry.slug), entry);
    } else if (value && typeof value === 'object') {
      for (const key of Object.keys(value)) take(key, value[key]);
    }
  }
  if (json.variantIds && typeof json.variantIds === 'object') {
    for (const key of Object.keys(json.variantIds)) take(key, json.variantIds[key]);
  }
  for (const key of PACK_KEYS) if (!out[key] && json[key]) out[key] = json[key];
  return out;
}

function applyCommerceJson(json) {
  commerce.storeOrigin = originOf(json.store) || originOf(json.storeUrl) || originOf(json.domain);
  if (isText(json.currency)) commerce.currency = json.currency.trim().toUpperCase();
  if (isText(json.productUrl)) commerce.productUrl = json.productUrl.trim();

  const entries = packEntriesFrom(json);
  const sharedHandle = textOf(json.productHandle) || textOf(json.handle);

  for (const key of PACK_KEYS) {
    const pack = packs.get(key);
    if (!pack) continue;
    const entry = entries[key];
    const obj = entry && typeof entry === 'object' ? entry : null;
    const raw = obj
      ? (obj.variantId != null ? obj.variantId
        : obj.variant_id != null ? obj.variant_id
        : obj.gid != null ? obj.gid
        : obj.id != null ? obj.id
        : null)
      : entry;

    pack.variantId = variantIdOf(raw);
    pack.handle = (obj && (textOf(obj.handle) || textOf(obj.productHandle))) || sharedHandle || null;
    pack.productUrl = (obj && textOf(obj.productUrl)) || null;
  }
}

/** The Shopify storefront AJAX product endpoint. On the store itself the same-origin
    path is the only one that exists; anywhere else (a preview host, a local build) the
    store origin is the one that can answer, so it goes first and same-origin is the
    fallback rather than a guaranteed 404 in the console. */
async function fetchShopifyProduct(handle) {
  const path = '/products/' + encodeURIComponent(handle) + '.js';
  const paths = [];
  if (commerce.storeOrigin && commerce.storeOrigin !== location.origin) paths.push(commerce.storeOrigin + path);
  paths.push(path);
  let lastError = null;
  for (const url of paths) {
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
      if (!res.ok) { lastError = new Error(url + ' responded ' + res.status); continue; }
      const json = await res.json();
      if (json && Array.isArray(json.variants)) return json;
      lastError = new Error(url + ' returned an unexpected payload');
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('could not reach ' + paths[0]);
}

function resetPacksToPending() {
  for (const [, pack] of packs) {
    pack.reason = 'pending';
    pack.available = false;
    pack.price = null;
  }
  renderCommerce();
}

async function loadCommerce() {
  if (commerce.inflight) return commerce.inflight;
  commerce.inflight = (async () => {
    resetPacksToPending();
    commerce.lastError = null;

    /* The Liquid template may inject the payload inline; when it does, that is the
       same data commerce.json holds, and it saves a request on the deployed page. */
    let json = (typeof window !== 'undefined' && window.HIYAZAKURA_COMMERCE) || null;
    if (!json || typeof json !== 'object') json = null;
    if (!json) try {
      const res = await fetch(new URL('./extoril-hiyazakura-commerce.json?v=82f3370d85c511b3', import.meta.url), { credentials: 'omit' });
      if (!res.ok) throw new Error('commerce.json responded ' + res.status);
      json = await res.json();
      if (!json || typeof json !== 'object') throw new Error('commerce.json is not an object');
    } catch (err) {
      /* Not written yet, or not reachable: an unlinked page, not a crash. */
      commerce.lastError = err;
      for (const [, pack] of packs) { pack.reason = 'unlinked'; pack.available = false; pack.price = null; }
      renderCommerce();
      console.warn('[hiyazakura] commerce.json unavailable — purchase blocked, no price shown.', err);
      return;
    }

    applyCommerceJson(json);
    /* commerce.json records the shop's own currency. Shopify Markets can present the
       visitor a different one (rate in window.Shopify.currency.rate), and the prices we
       just fetched are already in THAT currency — so the presentment currency always
       wins. Treating commerce.json as authoritative labelled euros as dollars. */
    if (window.Shopify && window.Shopify.currency && isText(window.Shopify.currency.active)) {
      commerce.currency = window.Shopify.currency.active.trim().toUpperCase();
    }
    /* Off the store (local preview, the artifact mirror) there is no window.Shopify, yet the
       prices fetched below are still in the visitor's market currency. Ask the store which one. */
    else if (commerce.storeOrigin && commerce.storeOrigin !== location.origin) {
      try {
        const res = await fetch(commerce.storeOrigin + '/cart.js', { credentials: 'omit' });
        if (res.ok) {
          const cart = await res.json();
          if (cart && isText(cart.currency)) commerce.currency = cart.currency.trim().toUpperCase();
        }
      } catch (_) { /* keep the currency commerce.json recorded */ }
    }

    const groups = new Map();
    for (const key of PACK_KEYS) {
      const pack = packs.get(key);
      if (!pack) continue;
      if (!pack.variantId || !pack.handle) {
        pack.reason = 'unlinked';
        pack.available = false;
        continue;
      }
      if (!groups.has(pack.handle)) groups.set(pack.handle, []);
      groups.get(pack.handle).push(pack);
    }

    await Promise.all(Array.from(groups.entries()).map(async ([handle, group]) => {
      try {
        const product = await fetchShopifyProduct(handle);
        for (const pack of group) {
          const variant = product.variants.find(v => v && String(v.id) === pack.variantId);
          if (!variant) { pack.reason = 'missing'; pack.available = false; continue; }
          const cents = Number(variant.price);
          if (!Number.isFinite(cents) || cents <= 0) { pack.reason = 'noprice'; pack.available = false; continue; }
          pack.price = cents;
          /* Compare-at is the merchant's own markdown; shown only when it is really higher. */
          const was = Number(variant.compare_at_price);
          pack.compareAt = Number.isFinite(was) && was > cents ? was : null;
          pack.available = variant.available !== false;
          pack.reason = pack.available ? 'ok' : 'soldout';
        }
      } catch (err) {
        commerce.lastError = err;
        for (const pack of group) { pack.reason = 'lookup'; pack.available = false; pack.price = null; }
        console.warn('[hiyazakura] live price lookup failed for "' + handle + '" — purchase blocked.', err);
      }
    }));

    renderCommerce();
  })().finally(() => { commerce.inflight = null; });

  return commerce.inflight;
}

/* The server's string when it is the same amount, otherwise Intl in the live currency. */
function moneyFor(pack, cents, which) {
  if (pack) {
    const isWas = which === 'was';
    const serverCents = isWas ? pack.serverWasCents : pack.serverCents;
    const serverMoney = isWas ? pack.serverWas : pack.serverMoney;
    if (serverMoney && Number(serverCents) === Number(cents)) return serverMoney;
  }
  return formatPrice(cents);
}

function formatPrice(cents) {
  const currency = commerce.currency || 'USD';
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(root.lang || 'en', { style: 'currency', currency }).format(amount);
  } catch (_) {
    return amount.toFixed(2) + ' ' + currency;
  }
}

const isSellable = pack =>
  !!pack && pack.reason === 'ok' && pack.available === true &&
  Number.isFinite(pack.price) && pack.price > 0 && !!pack.variantId;

function statusFor(pack) {
  if (!pack) return 'Select a set to continue.';
  switch (pack.reason) {
    case 'pending':
      return 'Checking live price and availability…';
    case 'ok':
      return 'Price read live from the Extoril store just now.';
    case 'soldout':
      return pack.label + ' is not available to order right now.';
    case 'missing':
      return 'We can’t match ' + pack.label + ' to a product in the Extoril store, so ordering is switched off.';
    case 'noprice':
      return 'The Extoril store has no published price for ' + pack.label + ' yet, so ordering is switched off.';
    case 'unlinked':
      return pack.label + ' isn’t connected to the Extoril store yet. There is no price to show, so there is no order to take.';
    case 'lookup':
    default:
      return 'We couldn’t reach the Extoril store to confirm the price, so checkout is switched off rather than showing you a guess.';
  }
}

function renderCommerce() {
  const section = $('.hz-packs');
  const buy = hz('buy');
  const btn = hz('buyBtn');
  const status = hz('buyStatus');
  const retry = hz('buyRetry');
  const variantInput = hz('buyVariant');
  const alt = hz('buyAlt');

  let anySellable = false;
  let anyPending = false;

  for (const [, pack] of packs) {
    const sellable = isSellable(pack);
    const pending = pack.reason === 'pending';
    anySellable = anySellable || sellable;
    anyPending = anyPending || pending;

    pack.card.classList.toggle('is-loading', pending);
    pack.card.classList.toggle('is-blocked', !pending && !sellable);

    if (pack.priceEl) {
      pack.priceEl.classList.toggle('is-loading', pending);
      pack.priceEl.classList.toggle('is-blocked', !pending && !sellable);
      /* The placeholder is a dash. It is never a number we made up. */
      setIfChanged(pack.priceEl, sellable ? moneyFor(pack, pack.price, 'price') : '—');
      if (sellable) pack.priceEl.removeAttribute('aria-label');
      else pack.priceEl.setAttribute('aria-label', pending ? 'Checking price' : 'Price unavailable');
    }
    /* The markdown, derived from the two live figures - never a stored percentage. */
    const deal = sellable && Number.isFinite(pack.compareAt) && pack.compareAt > pack.price;
    if (pack.dealEl) pack.dealEl.hidden = !deal;
    if (pack.wasEl) { pack.wasEl.hidden = !deal; if (deal) setIfChanged(pack.wasEl, moneyFor(pack, pack.compareAt, 'was')); }
    if (pack.saveEl) { pack.saveEl.hidden = !deal; if (deal) setIfChanged(pack.saveEl, '\u2212' + Math.round(100 - (pack.price * 100) / pack.compareAt) + '%'); }
  }

  if (section) section.classList.toggle('is-blocked', !anyPending && !anySellable);

  const pack = packs.get(selected);
  const sellable = isSellable(pack);
  const pending = !!pack && pack.reason === 'pending';
  const label = pack ? pack.label : 'HIYAZAKURA';

  if (buy) {
    buy.classList.toggle('is-loading', pending);
    buy.classList.toggle('is-blocked', !pending && !sellable);
    buy.action = (commerce.storeOrigin && commerce.storeOrigin !== location.origin)
      ? commerce.storeOrigin + '/cart/add'
      : '/cart/add';
  }

  if (variantInput) variantInput.value = sellable ? pack.variantId : '';

  if (btn) {
    btn.disabled = !sellable;
    btn.setAttribute('aria-disabled', String(!sellable));
    btn.classList.toggle('hz-buy__btn--disabled', !sellable);
    setIfChanged(btn, sellable
      ? 'Buy ' + label + ' — ' + moneyFor(pack, pack.price, 'price')
      : pending ? 'Buy ' + label
      : label + ' unavailable');
  }

  setIfChanged(status, statusFor(pack));
  if (retry) retry.hidden = !(pack && pack.reason === 'lookup');

  if (alt) {
    const href = (pack && pack.productUrl) || commerce.productUrl;
    if (isText(href)) alt.href = href;
    alt.textContent = 'Open ' + (pack ? 'HIYAZAKURA ' + label : 'HIYAZAKURA') + ' on extoril.com';
  }

  renderOrderExtras(pack, sellable, pending, label);
}

function initBuy() {
  const buy = hz('buy');
  const retry = hz('buyRetry');

  if (buy) {
    buy.addEventListener('submit', event => {
      const pack = packs.get(selected);
      if (!isSellable(pack)) {
        event.preventDefault();
        renderCommerce();
        return;
      }
      const variantInput = hz('buyVariant');
      if (variantInput) variantInput.value = pack.variantId;
      /* Native POST to /cart/add with return_to=/checkout: one request, straight
         to Shopify checkout, and it still works if this script later throws. */
    });
  }

  if (retry) retry.addEventListener('click', () => { loadCommerce(); });
}

/* ==========================================================================
   6 · BOOT
   ========================================================================== */

const year = hz('year');
if (year) year.textContent = String(new Date().getFullYear());

initPacks();
initBuy();
initChill();
armAutoplayAbort();
observeStage();

bootBlossom();
loadCopy();
loadCommerce();

/* ==========================================================================
   7 · FILM — the footage loop, played only while it is on screen
   --------------------------------------------------------------------------
   The <video> carries no `autoplay` attribute: with autoplay, browsers pull the
   whole file on page load whatever preload="metadata" says. Playing it from an
   IntersectionObserver keeps the download until the figure is actually in view,
   and pauses it when it scrolls away. Under reduced motion it never starts on its
   own; it shows native controls instead, so the footage is still one tap away.
   ========================================================================== */

function initFilm() {
  const videos = Array.from(doc.querySelectorAll('.hz-moment__video'));
  if (!videos.length) return;

  const still = () => {
    for (const v of videos) {
      v.removeAttribute('autoplay');
      v.removeAttribute('loop');
      try { v.pause(); } catch (_) { /* nothing was playing */ }
      v.controls = true;
    }
  };

  if (reducedMotion()) { still(); return; }

  try { motionQuery.addEventListener('change', () => { if (reducedMotion()) still(); }); }
  catch (_) { if (motionQuery.addListener) motionQuery.addListener(() => { if (reducedMotion()) still(); }); }

  const play = v => {
    if (reducedMotion()) return;
    const p = v.play();
    /* A refused play() (autoplay policy, data saver) is not an error: fall back to controls. */
    if (p && typeof p.catch === 'function') p.catch(() => { v.controls = true; });
  };

  if (typeof IntersectionObserver !== 'function') { videos.forEach(play); return; }

  const io = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const v = entry.target;
      if (entry.isIntersecting) play(v);
      else if (!v.paused) v.pause();
    }
  }, { threshold: 0.3 });
  for (const v of videos) io.observe(v);
}

try { initFilm(); } catch (_) { /* decorative; never fatal */ }

/* ==========================================================================
   ORDER CLARITY — summary, sticky bar, "from" price. All mirror the one source of
   truth (renderCommerce); none of them can show a figure the buy button does not.
   ========================================================================== */
function renderOrderExtras(pack, sellable, pending, label) {
  const cupsEl = pack && pack.card ? pack.card.querySelector('.hz-pack__cups') : null;
  const cups = cupsEl ? cupsEl.textContent.trim() : '';
  const money = sellable ? moneyFor(pack, pack.price, 'price') : '—';

  const name = hz('orderName'), price = hz('orderPrice');
  if (name) setIfChanged(name, cups ? label + ' · ' + cups : label);
  if (price) setIfChanged(price, money);

  const sName = hz('stickyName'), sPrice = hz('stickyPrice'), sBtn = hz('stickyBtn');
  if (sName) setIfChanged(sName, label);
  if (sPrice) setIfChanged(sPrice, money);
  if (sBtn) {
    sBtn.disabled = !sellable;
    setIfChanged(sBtn, sellable ? 'Buy ' + label : pending ? 'Checking…' : 'Unavailable');
  }

  const one = packs.get('one'), from = hz('heroFrom'), fromPrice = hz('heroFromPrice');
  if (from && fromPrice) {
    const ok = isSellable(one);
    from.hidden = !ok;
    if (ok) setIfChanged(fromPrice, moneyFor(one, one.price, 'price'));
  }
}

/* Hero gallery: thumbnails swap the framed photograph. */
(() => {
  const main = hz('galleryMain'), webp = hz('galleryWebp');
  const thumbs = Array.from(document.querySelectorAll('.hz-hero__thumb'));
  if (!main || !thumbs.length) return;
  for (const t of thumbs) {
    t.addEventListener('click', () => {
      const jpg = t.getAttribute('data-hz-photo-jpg');
      if (!jpg) return;
      const w = t.getAttribute('data-hz-photo-webp') || '';
      if (webp) { webp.srcset = w || jpg; webp.type = w ? 'image/webp' : 'image/jpeg'; }
      main.src = jpg;
      main.alt = t.getAttribute('data-hz-photo-alt') || '';
      for (const o of thumbs) {
        const on = o === t;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-pressed', String(on));
      }
    });
  }
})();

/* Sticky buy bar: only between the hero and the order block, never over either. */
(() => {
  const bar = hz('sticky'), hero = document.querySelector('.hz-hero'), order = hz('order');
  if (!bar || !hero || !('IntersectionObserver' in window)) return;
  let heroVisible = true, orderVisible = false;
  const sync = () => {
    bar.hidden = heroVisible || orderVisible;
    document.documentElement.classList.toggle('is-sticky-on', !bar.hidden);
  };
  new IntersectionObserver(es => { heroVisible = es[0].isIntersecting; sync(); }, { threshold: 0.05 }).observe(hero);
  if (order) new IntersectionObserver(es => { orderVisible = es[0].isIntersecting; sync(); }, { threshold: 0.2 }).observe(order);
})();

/* Reviews: rendered only from the store's own review metafields. No count, no stars, no text
   exists in this file — if the store has no reviews, the honest empty state stays. */
(() => {
  const box = hz('reviews');
  if (!box) return;
  const rating = Number(box.getAttribute('data-hz-rating'));
  const count = Number(box.getAttribute('data-hz-rating-count'));
  if (!(count > 0) || !(rating > 0)) return;
  const title = hz('reviewsTitle'), body = hz('reviewsBody');
  if (title) title.textContent = rating.toFixed(1) + ' out of 5';
  if (body) body.textContent = 'From ' + count + ' verified ' + (count === 1 ? 'review' : 'reviews') + ' on the Extoril store.';
})();
